import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ReactPaginate from 'react-paginate';
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import { fetchAllBis } from '../../catalog/infrastructure/CatalogService';
import { GtmService } from '../../../infrastructure/analytics/GtmService';
import { initial } from '../../../common/domain/RemoteData.model';
import { remoteDataWithSetter } from '../../../common/utils/remoteDataWithSetter';
import { RemoteDataUI } from '../../../common/ui/RemoteDataUI';
import { Cardsection } from '../../../ui';
import { CatalogItemCard } from '../../../common/ui/CatalogItemCard';
import { PriceWithFallback } from '../../../common/ui/PriceWithFallback';
import { ProductFlatPurchaseCardUI } from '../../../product/ui/ProductFlatPurchaseCardUI';
import { useComercialContext } from '../../../comercial/ComercialContext';
import { useDebouncedEffect } from '../../../common/utils/useDemouncedEffect';
import { useFilterContext } from '../../catalog/infrastructure/FilterContext';
import { FilterSection } from '../../catalog/ui/FilterSection';
import { parseQueryToFilters, serializeFiltersToQuery } from '../../catalog/infrastructure/filterUrlMapper';
import { useTenantContext } from '../../../tenant/TenantContext';
import {
    SearchWrapper,
    SearchContainer,
    SearchInput,
    SearchIconWrapper,
} from '../../catalog/ui/filter-panel/ProductSearchFilter.styles';

const PAGE_SIZE = 20;

// Color de "oferta" por defecto (LeonVet/Sallavet); cada tenant puede traer el suyo en
// la config (promoColor/promoColorSoft) — VeterLucus usa ambar porque el naranja no
// casaba con sus verdes.
const PROMO_ORANGE = '#f0541e';
const PROMO_SOFT = '#fdeee8';

// Banda fina: titulo + subtitulo a la izquierda y el buscador integrado a su altura,
// a la derecha (peticion usuario 1-sep; antes ocupaba tres alturas con el buscador debajo).
export const Hero = styled.div`
    background: ${({ $bg }) => $bg || PROMO_ORANGE};
    border-radius: 16px;
    padding: 1rem 1.5rem;
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    color: white;

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1.5rem;

    h1 {
        color: white;
        font-size: 1.6rem;
        margin-bottom: 0;
    }

    p {
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.9rem;
        margin-bottom: 0;
    }

    /* El SearchWrapper compartido trae width:100%; aqui debe convivir en fila con el
       titulo (crece hasta 540px y baja de linea solo cuando no cabe). */
    ${SearchWrapper} {
        flex: 1 1 280px;
        width: auto;
    }
`;

export const HeroTitles = styled.div`
    min-width: 0;
`;

const DivPagination = styled.div`
    padding: 1.5rem 0 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TabsBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 2rem;
`;

const TabButton = styled.button`
    border: 2px solid ${({ $promo }) => $promo || PROMO_ORANGE};
    border-radius: 999px;
    padding: 0.4rem 1.25rem;
    font-weight: 600;
    background: ${({ $active, $promo }) => ($active ? ($promo || PROMO_ORANGE) : 'white')};
    color: ${({ $active, $promo }) => ($active ? 'white' : ($promo || PROMO_ORANGE))};
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
        background: ${({ $active, $promo, $promoSoft }) =>
            ($active ? ($promo || PROMO_ORANGE) : ($promoSoft || PROMO_SOFT))};
    }
`;

// Estado inicial (busqueda + filtros del panel) leido de la URL, para que "volver" desde
// una ficha no haga empezar de cero. Reusa los mismos parametros que el catalogo (se, tmf...).
export const readStateFromParams = (searchParams) => {
    const parsed = parseQueryToFilters(searchParams);
    const panelFilter = {};
    if (parsed.taxonomyTree?.macrofamilyId || parsed.taxonomyTree?.familyId || parsed.taxonomyTree?.subfamilyIds?.size) {
        panelFilter.taxonomyTree = parsed.taxonomyTree;
    }
    if (parsed.manufacturerIds?.size) panelFilter.manufacturerIds = parsed.manufacturerIds;
    if (parsed.species?.size) panelFilter.species = parsed.species;
    if (parsed.characteristics?.prescription != null) {
        panelFilter.characteristics = { prescription: parsed.characteristics.prescription };
    }
    return { search: parsed.search || '', panelFilter };
};

const hasActivePanelFilters = (panelFilter) =>
    !!panelFilter?.taxonomyTree?.macrofamilyId
    || (panelFilter?.manufacturerIds?.size ?? 0) > 0
    || (panelFilter?.species?.size ?? 0) > 0
    || panelFilter?.characteristics?.prescription != null;

/**
 * Una seccion de la pagina de Promociones: cabecera + listado paginado de los productos que
 * cumplen la caracteristica (combinables, corta caducidad u ofertas), filtrados ademas por el
 * buscador propio de la pagina y por el panel lateral (categorias, fabricantes, especies,
 * prescripcion). Todo el filtrado se hace en el servidor reutilizando el endpoint del catalogo.
 */
export const PromoSection = ({ title, characteristics, search, panelFilter, viewMode, hideWhenEmpty, page: controlledPage, onPageChange }) => {
    const { actingAsCliente } = useComercialContext();
    const [remoteData, setRemoteData] = useState(initial(null));
    // La pagina puede venir controlada desde fuera (para sincronizarla con la URL);
    // si no, se gestiona internamente como siempre.
    const [internalPage, setInternalPage] = useState(0);
    const page = controlledPage ?? internalPage;
    const setPage = onPageChange ?? setInternalPage;

    // Al cambiar busqueda o filtros se vuelve a la pagina 0 — pero NO en el primer
    // render, para no pisar la pagina que llega de la URL al volver de una ficha.
    const isFirstRun = React.useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        setPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, panelFilter]);

    useEffect(() => {
        const actingAsNumCliente = actingAsCliente?.numCliente ?? null;
        const fetchFn = () => fetchAllBis(
            {
                search: search || null,
                taxonomyTree: panelFilter?.taxonomyTree,
                manufacturerIds: panelFilter?.manufacturerIds,
                species: panelFilter?.species,
                characteristics: {
                    ...characteristics,
                    prescription: panelFilter?.characteristics?.prescription,
                },
            },
            { page, amount: PAGE_SIZE },
            actingAsNumCliente
        );
        remoteDataWithSetter(setRemoteData, fetchFn).then();
    }, [search, panelFilter, page, actingAsCliente, characteristics]);

    const filtered = !!search || hasActivePanelFilters(panelFilter);

    return (
        <RemoteDataUI remoteData={remoteData}>
            <RemoteDataUI.Pending>
                <div className="text-center m-4"><Spinner /></div>
            </RemoteDataUI.Pending>

            <RemoteDataUI.Failure>
                <div className="alert alert-danger">Imposible cargar productos. Intentelo más tarde.</div>
            </RemoteDataUI.Failure>

            <RemoteDataUI.Success>
                {catalog => {
                    // En la vista "Todas", una seccion sin productos y sin filtros se oculta
                    // entera (igual que en la portada). Con su pestana seleccionada o con
                    // filtros/busqueda activos se muestra el aviso.
                    if (catalog.content.length === 0 && !filtered && hideWhenEmpty) return null;

                    return (
                        <section className="mb-4">
                            <div className="border-start border-4 border-primary ps-3 mb-3">
                                <p className="fw-bolder text-primary fs-5 mb-0">{title}</p>
                            </div>

                            {catalog.content.length === 0 ? (
                                <p className="text-muted ms-1">
                                    {search
                                        ? <>Sin resultados para «{search}» en esta sección.</>
                                        : filtered
                                            ? <>Sin resultados con los filtros aplicados en esta sección.</>
                                            : <>Ahora mismo no hay productos en esta sección.</>}
                                </p>
                            ) : (
                                <>
                                    {viewMode === 'grid' ? (
                                        <Cardsection>
                                            {catalog.content.map((product) => (
                                                <div key={product.id ?? product.code}>
                                                    <CatalogItemCard data={{ catalogItem: product }}>
                                                        <CatalogItemCard.Price>
                                                            <PriceWithFallback data={{
                                                                sellingPrice: product.price?.bestPrice,
                                                                basePrice: product.price?.value
                                                            }} />
                                                        </CatalogItemCard.Price>
                                                    </CatalogItemCard>
                                                </div>
                                            ))}
                                        </Cardsection>
                                    ) : (
                                        <div className="cardList">
                                            {catalog.content.map((product) => (
                                                <div key={product.id ?? product.code} className="my-1">
                                                    <ProductFlatPurchaseCardUI details={product} />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {catalog.totalPages > 1 && (
                                        <DivPagination>
                                            <ReactPaginate
                                                previousLabel="<"
                                                nextLabel=">"
                                                pageClassName="page-item"
                                                pageLinkClassName="page-link"
                                                previousClassName="page-item"
                                                previousLinkClassName="page-link"
                                                nextClassName="page-item"
                                                nextLinkClassName="page-link"
                                                breakLabel="..."
                                                breakClassName="page-item"
                                                breakLinkClassName="page-link"
                                                pageCount={catalog.totalPages}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={5}
                                                onPageChange={(selectedItem) => setPage(selectedItem.selected)}
                                                containerClassName="pagination"
                                                activeClassName="active"
                                                forcePage={catalog.pageable.pageNumber}
                                            />
                                        </DivPagination>
                                    )}
                                </>
                            )}
                        </section>
                    );
                }}
            </RemoteDataUI.Success>
        </RemoteDataUI>
    );
};

const COMBINABLE_CHARACTERISTICS = { hasCombinable: true };
const LOT_PROMOTION_CHARACTERISTICS = { hasActiveLotPromotion: true };
const DEALS_CHARACTERISTICS = { hasDeals: true };

const SECTIONS = [
    { key: 'combinables', tabLabel: 'Combinables', title: 'OFERTAS COMBINABLES', characteristics: COMBINABLE_CHARACTERISTICS },
    { key: 'caducidad', tabLabel: 'Corta caducidad', title: 'OFERTAS CORTA CADUCIDAD', characteristics: LOT_PROMOTION_CHARACTERISTICS },
    { key: 'ofertas', tabLabel: 'Ofertas', title: 'OFERTAS', characteristics: DEALS_CHARACTERISTICS },
];

export const PromocionesPage = () => {
    const { filterOptionsRemoteData } = useFilterContext();
    const { promoColor, promoColorSoft } = useTenantContext();
    const [searchParams, setSearchParams] = useSearchParams();
    // El estado inicial sale de la URL: asi el boton "volver" del navegador restaura
    // busqueda, pestaña, filtros y pagina tal y como estaban.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialState = useMemo(() => ({
        ...readStateFromParams(searchParams),
        tab: searchParams.get('tab') || 'todas',
        page: parseInt(searchParams.get('p') || '0', 10) || 0,
    }), []);

    const [searchInput, setSearchInput] = useState(initialState.search);
    const [search, setSearch] = useState(initialState.search);
    const [activeTab, setActiveTab] = useState(initialState.tab);
    // Pagina de la pestaña activa (solo se sincroniza con la URL fuera de "Todas").
    const [activePage, setActivePage] = useState(initialState.page);
    // Filtros del panel lateral, LOCALES a esta pagina: no tocan el FilterContext global
    // para no arrastrar filtros al catalogo (ni traerlos de alli).
    const [panelFilter, setPanelFilter] = useState(initialState.panelFilter);
    const [viewMode, setViewMode] = useState('grid');

    useDebouncedEffect(() => {
        setSearch(searchInput.trim());
    }, [searchInput], 300);

    // Estado → URL (replace para no ensuciar el historial con cada tecla).
    useEffect(() => {
        const params = serializeFiltersToQuery({ search, ...panelFilter });
        // El cp del panel ya va en serializeFiltersToQuery; tab y p son propios de esta pagina.
        if (activeTab !== 'todas') {
            params.set('tab', activeTab);
            if (activePage > 0) params.set('p', String(activePage));
        }
        setSearchParams(params, { replace: true });
    }, [search, panelFilter, activeTab, activePage, setSearchParams]);

    const onTabChange = (tab) => {
        setActiveTab(tab);
        setActivePage(0);
    };

    // Analitica: vista de la seccion y de cada cambio de pestaña.
    useEffect(() => {
        GtmService.logSectionView('ofertas', activeTab);
    }, [activeTab]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            setSearch(searchInput.trim());
        }
    };

    return (
        <div className="container">
            <Hero $bg={promoColor}>
                <HeroTitles>
                    <h1>Ofertas</h1>
                    <p>Todas las promociones en un solo sitio: combinables, corta caducidad y ofertas.</p>
                </HeroTitles>

                <SearchWrapper>
                    <SearchContainer>
                        <SearchInput
                            placeholder="Busca dentro de las ofertas"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            type="text"
                        />
                        <SearchIconWrapper onClick={() => setSearch(searchInput.trim())}>
                            <SearchIcon />
                        </SearchIconWrapper>
                    </SearchContainer>
                </SearchWrapper>
            </Hero>

            <TabsBar>
                <TabButton
                    $active={activeTab === 'todas'}
                    $promo={promoColor} $promoSoft={promoColorSoft}
                    onClick={() => onTabChange('todas')}
                >
                    Todas
                </TabButton>
                {SECTIONS.map(section => (
                    <TabButton
                        key={section.key}
                        $active={activeTab === section.key}
                        $promo={promoColor} $promoSoft={promoColorSoft}
                        onClick={() => onTabChange(section.key)}
                    >
                        {section.tabLabel}
                    </TabButton>
                ))}
            </TabsBar>

            <div className="row">
                {/* En movil el panel de filtros se oculta entero (decision usuario 11-ago). */}
                <aside className="col-12 col-lg-3 mb-4 d-none d-lg-block">
                    <RemoteDataUI remoteData={filterOptionsRemoteData}>
                        <RemoteDataUI.Pending>
                            <div className="text-center"><Spinner /></div>
                        </RemoteDataUI.Pending>

                        <RemoteDataUI.Failure>
                            <div className="alert alert-danger">
                                Imposible cargar Filtros. Intentelo más tarde.
                            </div>
                        </RemoteDataUI.Failure>

                        <RemoteDataUI.Success>
                            {filterOptions =>
                                <FilterSection
                                    filterOptions={filterOptions}
                                    filter={panelFilter}
                                    viewMode={viewMode}
                                    onFilterChange={setPanelFilter}
                                    onViewModeChange={setViewMode}
                                />
                            }
                        </RemoteDataUI.Success>
                    </RemoteDataUI>
                </aside>

                <main className="col-12 col-lg-9 p-0">
                    {SECTIONS
                        .filter(section => activeTab === 'todas' || activeTab === section.key)
                        .map(section => (
                            <PromoSection
                                key={section.key}
                                title={section.title}
                                characteristics={section.characteristics}
                                search={search}
                                panelFilter={panelFilter}
                                viewMode={viewMode}
                                hideWhenEmpty={activeTab === 'todas'}
                                page={activeTab === section.key ? activePage : undefined}
                                onPageChange={activeTab === section.key ? setActivePage : undefined}
                            />
                        ))}
                </main>
            </div>
        </div>
    );
};
